<template>

  <section class="flex flex-col gap-y-4">
    <card class="flex flex-col gap-y-5 rounded-lg" :bg="!noCard" :shadow="!noCard" :padding="!noCard ? 'p-3':''">
      <section v-if="title" :class="titleContainerClass">
        <span :class="titleClass">
          {{ title }}
        </span>
        <slot name="functions"></slot>
      </section>
      <section class="overflow-x-auto">
        <table class="border-collapse min-w-full w-max" :id="tableId">
          <thead>
            <tr>
              <th v-if="order">
                #
              </th>
              <th
                v-for="(item, idx) in columns" :key="idx"
                class="text-uppercase text-xxs font-bolder text-center px-2"
                :class="[
                  idx === 0 && freezeL ? 'sticky left-0 bg-white':'',

                  idx === columns.length-1
                  && !validSlot('actions')
                  && freezeR ?
                    'sticky right-0 bg-white':
                    ''
                ]"
              >
                <slot :name="`header:${item.field}`">
                  {{ item.label }}
                </slot>
              </th>

              <th
                v-if="validSlot('actions')"
                class="text-uppercase text-xxs font-bolder text-center px-2"
                :class="freezeR ? 'sticky right-0 bg-white':''"
              >
                Ações
              </th>
            </tr>
          </thead>

          <tbody>
            <template v-if="loading">
              <tr v-if="spinnerLoading">
                <td :colspan="colspan">
                  <section class="flex text-center justify-center items-center p-5">
                    <rpm-spinner />
                  </section>
                </td>
              </tr>

              <tr v-else v-for="line in (pagination.per_page ?? 1)">
                <td class="p-2 border-y border-slate-200" v-for="cell in (columns.length + (order ? 1:0) + (validSlot('actions') ? 1:0))">
                  <div class="w-full min-h-[1.2rem] animate-pulse bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                </td>
              </tr>
            </template>

            <tr v-else-if="data.length === 0" class="text-sm text-center justify-center">
              <td :colspan="colspan" class="border-slate-200" :class="!validSlot('footer') ? 'border-t':'border-y'">
                {{ emptyText }}
              </td>
            </tr>

            <tr
              v-else
              v-for="(item, trDataIdx) in data" :key="trDataIdx"
              :id="lineId ? ('line-' + item[lineId]):''"
            >
              <td v-if="order" class="p-2 border-y border-slate-200">
                <div class="px-2 text-xs w-fit">
                  <span>
                    {{ trDataIdx+1 }}
                  </span>
                </div>
              </td>
              <td
                v-for="(column, tdColIdx) in columns" :key="tdColIdx"
                :class="[
                    'p-2 border-y border-slate-200',
                    tdColIdx === 0 ? [
                      'pl-4',
                      freezeL ? 'drop-shadow-[2px_0px_2px_rgba(0,0,0,0.1)] sticky left-0 bg-white':''
                    ]:'',
                    tdColIdx === columns.length-1 ? [
                      'pr-4',
                      freezeR && !validSlot('actions') ?
                        'drop-shadow-[-2px_0px_2px_rgba(0,0,0,0.1)] sticky right-0 bg-white':''
                    ]:'',
                ]"
              >
                <slot
                  :name="`cell:${column.field}`"
                  :item="item"
                  :data="getField(item, column.key)"
                >
                  <div
                    class="px-2 text-xs"
                    :class="[getClass(item, column), column.class]"
                  >
                    <span
                      v-html="getField(item, column.key, getText(item, column)) "
                      :class="!item.enabled && ('enabled' in item) ? 'text-line' : ''"
                    ></span>
                  </div>
                </slot>
              </td>

              <td
                v-if="validSlot('actions')"
                class="py-2 px-4 border-y border-slate-200"
                :class="freezeR ? 'drop-shadow-[-2px_0px_2px_rgba(0,0,0,0.1)] sticky right-0 bg-white':''"
              >
                <div class="flex flex-row gap-2 items-center justify-center">
                  <slot
                    name="actions"
                    :item="item"
                  ></slot>
                </div>
              </td>
            </tr>

            <tr v-if="validSlot('footer')">
              <slot name="footer"></slot>
            </tr>
          </tbody>
        </table>
      </section>
    </card>

    <section class="flex flex-row justify-center">
      <rpm-pagination
        text-class="text-xs"
        :pagination="pagination"
        @change-page="changePage"
      ></rpm-pagination>
    </section>
  </section>
</template>

<script>

import _ from "lodash";
import moment from 'moment';
import RpmSpinner from "#components/RpmSpinner.vue";
import RpmPagination from "#components/RpmPagination.vue";

export default {
  name: "RpmTable",
  components: {
    RpmPagination,
    RpmSpinner
  },
  data() {
    return {
      colspan: this.columns.length + (this.validSlot('actions') ? 1:0) + (this.order  ? 1:0)
    };
  },
  props: {
    columns: {
      type: Array,
      default: [],
    },
    data: {
      type: Array,
      default: []
    },
    title: {
      type: String,
      default: ''
    },
    titleClass: {
      type: String,
      default: 'text-lg font-semibold whitespace-nowrap'
    },
    titleContainerClass: {
      type: String,
      default: 'p-4 flex flex-row items-center'
    },
    tableId: {
      type: String,
      default: ''
    },
    noCard: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    spinnerLoading: {
      type: Boolean,
      default: false,
    },
    freezeL: {
      type: Boolean,
      default: false,
    },
    freezeR: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Nenhum dado encontrado'
    },
    loadingText: {
      type: String,
      default: 'Carregando...'
    },
    lineId: {
      type: String,
      default: ''
    },
    pagination: {
      type: Object,
      default: {}
    },
  },
  methods: {
    getClass(item, label) {
      if (typeof item[label.field] === "boolean"
      || typeof item[label.field] === "number") {
          return "text-center";
      }

      return "";
    },
    getText(item, label) {
      if (typeof item[label.field] == "boolean")
        return this.getBooleanText(item, label);

      if (moment(item[label.field], ['DD/MM/YYYY'], true).isValid())
        return this.getDateText(item, label)

      return _.get(item, label.field);
    },
    getField(item, field, def) {
      return item[field] || def;
    },
    getBooleanText(item, label) {
      if (item[label.field] === true) return "Sim";
      return "Não";
    },
    getDateText(item, label) {
      return moment(item[label.field]).format('DD/MM/YYYY')
    },
    validSlot(slot) {
      return !!this.$slots[slot];
    },
    changePage(label){
      this.$emit('change-page', label);
    }
  }
};
</script>


